import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Semoji reactions
const semojis = [
    { type: 'goal', emoji: '⚽', label: 'Goal!' },
    { type: 'fire', emoji: '🔥', label: 'Fire!' },
    { type: 'clap', emoji: '👏', label: 'Clap' },
    { type: 'red_card', emoji: '🟥', label: 'Red Card' },
    { type: 'yellow_card', emoji: '🟨', label: 'Yellow' },
];

const MatchCard = ({ match, onUpdate }) => {
    const [showReactions, setShowReactions] = useState(false);
    const [reactions, setReactions] = useState(match.reactions || {});
    const navigate = useNavigate();

    const handleReaction = async (reactionType) => {
        try {
            const response = await axios.post(`${API}/matches/${match.id}/react`, {
                reaction_type: reactionType
            });
            setReactions(response.data.reactions);
            toast.success('Reaction sent!');
            setShowReactions(false);
        } catch (error) {
            console.error('Error sending reaction:', error);
            toast.error('Failed to send reaction');
        }
    };

    const handleCardClick = () => {
        // Only navigate to Live Match Room for LIVE matches
        if (match.status === 'live') {
            navigate(`/match/${match.id}`);
        }
    };

    const handleTeamClick = (e, teamName) => {
        e.stopPropagation(); // Prevent card click from firing
        // Navigate to Team Hub
        navigate(`/team/${encodeURIComponent(teamName)}`);
    };

    const handleReactClick = (e) => {
        e.stopPropagation(); // Prevent card click
        // For non-live matches, show the simple emoji popup
        if (match.status !== 'live') {
            setShowReactions(true);
        }
    };

    const getStatusBadge = () => {
        switch (match.status) {
            case 'live':
                return (
                    <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 bg-live rounded-full live-pulse" />
                        <span className="text-live text-xs font-bold uppercase">Live</span>
                    </div>
                );
            case 'finished':
                return <span className="text-white/50 text-xs font-medium uppercase">FT</span>;
            case 'upcoming':
                return <span className="text-white/50 text-xs font-medium uppercase">Soon</span>;
            default:
                return null;
        }
    };

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-card-bg border border-border rounded-xl p-4 ${
                    match.status === 'live' ? 'cursor-pointer hover:border-primary/50 transition-colors' : ''
                }`}
                onClick={handleCardClick}
                data-testid={`match-card-${match.id}`}
            >
                {/* League & Status */}
                <div className="flex items-center justify-between mb-3">
                    <span className="text-white/60 text-xs font-medium">{match.league}</span>
                    <div className="flex items-center gap-2">
                        {getStatusBadge()}
                        {match.status === 'live' && (
                            <span className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                                Join Room →
                            </span>
                        )}
                    </div>
                </div>

                {/* Teams & Score */}
                <div className="flex items-center justify-between">
                    {/* Home Team - Clickable */}
                    <button
                        onClick={(e) => handleTeamClick(e, match.home_team)}
                        className="flex-1 flex flex-col items-center group cursor-pointer hover:scale-105 transition-transform"
                        data-testid={`team-home-${match.id}`}
                    >
                        <div className="w-14 h-14 rounded-full bg-surface border-2 border-border flex items-center justify-center overflow-hidden mb-2 group-hover:ring-2 group-hover:ring-primary/50 transition-all shadow-lg">
                            <img 
                                src={match.home_logo} 
                                alt={match.home_team}
                                className="w-full h-full object-cover rounded-full"
                                onError={(e) => {
                                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(match.home_team)}&background=169239&color=fff&rounded=true&bold=true`;
                                }}
                            />
                        </div>
                        <span className="text-white text-xs font-medium text-center line-clamp-1 group-hover:text-primary transition-colors">
                            {match.home_team}
                        </span>
                    </button>

                    {/* Score */}
                    <div className="flex-1 flex flex-col items-center">
                        <div className="flex items-center gap-2">
                            <span className="font-heading text-4xl font-bold text-white">
                                {match.home_score}
                            </span>
                            <span className="text-white/30 text-2xl">-</span>
                            <span className="font-heading text-4xl font-bold text-white">
                                {match.away_score}
                            </span>
                        </div>
                        {match.status === 'live' && (
                            <span className="text-live text-sm font-heading font-semibold mt-1">
                                {match.minute}'
                            </span>
                        )}
                    </div>

                    {/* Away Team - Clickable */}
                    <button
                        onClick={(e) => handleTeamClick(e, match.away_team)}
                        className="flex-1 flex flex-col items-center group cursor-pointer hover:scale-105 transition-transform"
                        data-testid={`team-away-${match.id}`}
                    >
                        <div className="w-14 h-14 rounded-full bg-surface border-2 border-border flex items-center justify-center overflow-hidden mb-2 group-hover:ring-2 group-hover:ring-primary/50 transition-all shadow-lg">
                            <img 
                                src={match.away_logo} 
                                alt={match.away_team}
                                className="w-full h-full object-cover rounded-full"
                                onError={(e) => {
                                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(match.away_team)}&background=169239&color=fff&rounded=true&bold=true`;
                                }}
                            />
                        </div>
                        <span className="text-white text-xs font-medium text-center line-clamp-1 group-hover:text-primary transition-colors">
                            {match.away_team}
                        </span>
                    </button>
                </div>

                {/* Reaction Button - Different behavior for live vs non-live */}
                {match.status === 'live' ? (
                    <div className="mt-4 py-2 bg-primary/10 border border-primary/30 rounded-lg text-center">
                        <span className="text-primary text-sm font-medium">
                            Tap to enter Live Match Room 🎙️
                        </span>
                    </div>
                ) : (
                    <button
                        onClick={handleReactClick}
                        className="w-full mt-4 py-2 bg-surface hover:bg-white/10 border border-border rounded-lg text-white/80 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        data-testid={`react-button-${match.id}`}
                    >
                        <span>React</span>
                        <div className="flex gap-0.5">
                            {Object.entries(reactions).slice(0, 3).map(([type, count]) => (
                                <span key={type} className="text-xs">
                                    {semojis.find(s => s.type === type)?.emoji || '👏'}
                                </span>
                            ))}
                        </div>
                    </button>
                )}
            </motion.div>

            {/* Reactions Modal - Only for non-live matches */}
            <Dialog open={showReactions} onOpenChange={setShowReactions}>
                <DialogContent className="bg-card-bg border-border max-w-sm" data-testid="reactions-modal">
                    <DialogHeader>
                        <DialogTitle className="text-white font-heading text-xl">
                            Send Semoji
                        </DialogTitle>
                        <p className="text-white/60 text-sm">React to this match</p>
                    </DialogHeader>
                    <div className="grid grid-cols-5 gap-2 mt-4">
                        {semojis.map((semoji) => (
                            <button
                                key={semoji.type}
                                onClick={() => handleReaction(semoji.type)}
                                className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-surface transition-colors"
                                data-testid={`semoji-${semoji.type}`}
                            >
                                <span className="text-3xl">{semoji.emoji}</span>
                                <span className="text-xs text-white/60">{semoji.label}</span>
                                {reactions[semoji.type] > 0 && (
                                    <span className="text-xs text-primary font-medium">
                                        {reactions[semoji.type]}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MatchCard;
